import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { searchRepo } from "../api.js";
import { repoHref } from "../data/repo.js";
import { useDebounced } from "../data/useDebounced.js";
import { useShortcuts } from "../data/shortcuts.js";
import type {
  SearchMatch,
  SearchResponse,
  SearchTokenExpansion,
} from "../../shared/types.js";

const MD = /\.(md|markdown)$/;

/** One file's hits within a repo — the unit the combobox navigates over. */
interface Hit {
  repo: string;
  path: string;
  matches: SearchMatch[];
  truncated: boolean;
}

/**
 * Header search: a permanent box that opens a floating combobox of results
 * underneath it (portalled to <body> and pinned under the input). Typing ≥2
 * chars searches the repos it was handed (grep or fts) — the ones the sidebar
 * is showing, so hidden repos stay out unless you're in one; ↑/↓ move the
 * highlighted result and Enter opens it — carrying the query as `?find=` so the
 * page highlights it — and Esc closes. Modeled on the query picker in the SQL embed editor. The "?"
 * shortcut focuses the box.
 */
export function HeaderSearch({ repos }: { repos: string[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  // A backend failure used to be swallowed into a bare "no matches" — which is
  // how a dead fts index went unnoticed in production. Keep it and show it.
  const [errors, setErrors] = useState<string[]>([]);
  const [expansions, setExpansions] = useState<SearchTokenExpansion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  // The floating panel is portalled to <body> (so nothing clips it), so it must
  // be positioned by hand — pinned under the input.
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);

  const q = query.trim();
  const debounced = useDebounced(q, 250);
  const reposKey = repos.join(",");
  const multiRepo = repos.length > 1;

  // "?" jumps to the search box (the hook ignores keystrokes while a field is
  // focused, so it never fires while you're already typing here or editing).
  useShortcuts([{ key: "?", handler: () => inputRef.current?.focus() }]);

  // Search every repo for the debounced query; aggregate to one hit per file.
  // Wait for all repos (Promise.all) so the list — and the ↑/↓ cursor over it —
  // doesn't reshuffle under the user as individual repos land.
  useEffect(() => {
    if (debounced.length < 2) {
      setHits([]);
      setErrors([]);
      setExpansions([]);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    Promise.all(
      repos.map((repo) =>
        searchRepo(repo, debounced)
          .then((data): [string, SearchResponse | Error] => [repo, data])
          .catch((err): [string, SearchResponse | Error] => [
            repo,
            err instanceof Error ? err : new Error(String(err)),
          ]),
      ),
    ).then((responses) => {
      if (!live) return;
      const out: Hit[] = [];
      const errs: string[] = [];
      const expanded: SearchTokenExpansion[] = [];
      for (const [repo, data] of responses) {
        if (data instanceof Error) {
          errs.push(`${repo}: ${data.message}`);
          continue;
        }
        for (const [path, matches] of groupByFile(data.matches)) {
          out.push({ repo, path, matches, truncated: data.truncated });
        }
        // Only tokens the backend actually widened are worth reporting.
        for (const t of data.expansion?.tokens ?? []) {
          if (t.via === "prefix" || t.via === "typo") expanded.push(t);
        }
      }
      setHits(out);
      setErrors(errs);
      setExpansions(expanded);
      setActive(0);
      setLoading(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, reposKey]);

  // Pin the panel under the input; reposition on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return;
    }
    const place = (): void => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setBox({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  // Keep the highlighted row in view as the cursor moves.
  useEffect(() => {
    if (open) activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [open, active, hits]);

  /**
   * What the opened page should highlight. Normally the typed query — but when a
   * token was widened (prefix/typo), the typed text may not occur in the document
   * at all, so `?find=quoka` would highlight nothing and scroll nowhere. Swap each
   * widened token for the term that actually matched.
   */
  const findText = (): string => {
    if (!expansions.length) return debounced;
    const sub = new Map(expansions.map((t) => [t.token, t.terms[0]]));
    return debounced.replace(/[a-z0-9]+/gi, (w) => sub.get(w.toLowerCase()) ?? w);
  };

  const go = (hit: Hit): void => {
    navigate(
      `${repoHref(hit.repo, hit.path.replace(MD, ""))}?find=${encodeURIComponent(findText())}`,
    );
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open && hits[active]) {
        e.preventDefault();
        go(hits[active]);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      } else if (query) {
        setQuery("");
      }
    }
  };

  const showMenu = open && box && q.length >= 2;

  return (
    <div className="dw-header-search">
      <input
        ref={inputRef}
        className="dw-header-search-input"
        type="text"
        value={query}
        placeholder="Search…"
        aria-label="Search"
        role="combobox"
        aria-expanded={!!showMenu}
        aria-controls="dw-search-menu"
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        // Close on blur, but a tick late so a click on a result still lands.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {query && (
        <button
          type="button"
          className="dw-search-clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setQuery("");
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
        >
          ×
        </button>
      )}
      {showMenu &&
        createPortal(
          <div
            id="dw-search-menu"
            className="dw-search-menu"
            role="listbox"
            style={{ left: box.left, top: box.top, width: box.width }}
            // Keep focus in the input so the blur-close logic still governs.
            onMouseDown={(e) => e.preventDefault()}
          >
            {/* A failed backend is an operational fact, not an empty result. */}
            {errors.length > 0 && (
              <p className="dw-search-error" role="alert">
                search failed — {errors[0]}
                {errors.length > 1 ? ` (+${errors.length - 1} more)` : ""}
              </p>
            )}
            {expansions.length > 0 && (
              <p className="dw-muted dw-search-expanded">
                {expansions
                  .map((t) => `${t.token} → ${t.terms.slice(0, 3).join(", ")}`)
                  .join(" · ")}
              </p>
            )}
            {hits.length === 0 ? (
              <p className="dw-muted dw-search-empty">
                {loading ? "searching…" : errors.length ? "search unavailable" : "no matches"}
              </p>
            ) : (
              <ul className="dw-search-options">
                {hits.map((hit, i) => (
                  <li key={`${hit.repo}:${hit.path}`}>
                    <button
                      ref={i === active ? activeRef : null}
                      type="button"
                      role="option"
                      aria-selected={i === active}
                      className={"dw-search-option" + (i === active ? " active" : "")}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(hit)}
                    >
                      <span className="dw-search-option-head">
                        <span className="dw-search-file">
                          {multiRepo && <span className="dw-search-repo">{hit.repo}/</span>}
                          {hit.path}
                        </span>
                        <span className="dw-muted dw-search-count">
                          {hit.matches.length}
                          {hit.truncated ? "+" : ""}
                        </span>
                      </span>
                      <Snippet text={hit.matches[0].text} query={debounced} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function groupByFile(matches: SearchMatch[]): [string, SearchMatch[]][] {
  const map = new Map<string, SearchMatch[]>();
  for (const m of matches) {
    const arr = map.get(m.path);
    if (arr) arr.push(m);
    else map.set(m.path, [m]);
  }
  return [...map];
}

/** A windowed snippet around the first match, the query highlighted; the full
 * line is the `title` (hover shows it floating). */
function Snippet({ text, query }: { text: string; query: string }) {
  const ql = query.toLowerCase();
  const hit = text.toLowerCase().indexOf(ql);
  const PAD = 14;
  let body = text;
  let lead = false;
  let tail = false;
  if (hit >= 0) {
    const start = Math.max(0, hit - PAD);
    const end = Math.min(text.length, hit + query.length + 44);
    lead = start > 0;
    tail = end < text.length;
    body = text.slice(start, end);
  } else if (text.length > 60) {
    body = text.slice(0, 60);
    tail = true;
  }
  return (
    <span className="dw-search-snip" title={text}>
      {lead && "…"}
      {highlight(body, ql, query.length)}
      {tail && "…"}
    </span>
  );
}

/** Wrap each case-insensitive occurrence of the query in <mark>. */
function highlight(body: string, ql: string, len: number): ReactNode[] {
  if (!ql) return [body];
  const out: ReactNode[] = [];
  const bl = body.toLowerCase();
  let p = 0;
  let i = bl.indexOf(ql);
  while (i >= 0) {
    if (i > p) out.push(body.slice(p, i));
    out.push(<mark key={i}>{body.slice(i, i + len)}</mark>);
    p = i + len;
    i = bl.indexOf(ql, p);
  }
  if (p < body.length) out.push(body.slice(p));
  return out;
}
